import { type Property, type User } from '@kalos/kalos-rpc';
import { Button } from '@kalos/ui';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';

import { useContractQuery } from '../../../../../hooks/react-query/useContractsQuery';
import { useEventBatchGetQuery } from '../../../../../hooks/react-query/useEventsQuery';
import { useUserQuery } from '../../../../../hooks/react-query/useUserQuery';
import { contractBadgeStatus } from '../../../../../tools/contractStatus';
import { useSchedulerStyle } from '../../context/schedulerStyle';
import { useOutsideServiceArea } from '../../hooks/useOutsideServiceArea';
import { useRecentCompletedVisit } from '../../hooks/useRecentCompletedVisit';
import { CallbackPrompt } from '../CallbackPrompt';
import { OutsideServiceAreaBanner } from '../OutsideServiceAreaBanner';
import { EndWithoutSchedulingDialog } from './EndWithoutSchedulingDialog';
import { ReviewCustomerCard } from './ReviewCustomerCard';
import { ReviewInternalNote } from './ReviewInternalNote';
import { ReviewNotifications } from './ReviewNotifications';
import { ReviewServiceHistory } from './ReviewServiceHistory';

type JobType = 'service' | 'project';

interface ReviewStepProps {
  customer: User;
  property: Property;
  onBack: () => void;
  onContinue: (jobType: JobType) => void;
  onNotificationSaved: (type: 'customer' | 'property', newText: string) => void;
  onEndWithoutScheduling?: () => void;
  allowedJobType?: JobType;
  /** Whether the in-progress job is already flagged as a callback */
  isCallbackFlagged?: boolean;
  /** Flag the job as a callback against the given original job (carries to Step 3) */
  onMarkCallback?: (originalEventId: number) => void;
  /** Whether the recent-visit callback prompt has been dismissed (shared across steps) */
  callbackPromptDismissed?: boolean;
  /** Dismiss the recent-visit callback prompt (persists to Step 3) */
  onDismissCallbackPrompt?: () => void;
}

export const ReviewStep = ({
  customer,
  property,
  onBack,
  onContinue,
  onNotificationSaved,
  onEndWithoutScheduling,
  allowedJobType,
  isCallbackFlagged,
  onMarkCallback,
  callbackPromptDismissed = false,
  onDismissCallbackPrompt,
}: ReviewStepProps) => {
  // --- Data fetching (parallel) ---

  const contractQuery = useContractQuery({
    filter: {
      userId: customer.id,
      isActive: 1,
      orderBy: 'contract_date_created',
      orderDir: 'desc',
    },
    enabled: !!customer.id,
  });
  const contract = contractQuery.data?.id ? contractQuery.data : null;

  const contractStatus = contract ? contractBadgeStatus(contract) : null;

  // Re-fetch customer by ID to get fresh data (notification may have been added after selection)
  const freshCustomerQuery = useUserQuery({
    filters: { id: customer.id },
    enabled: !!customer.id,
  });
  const freshCustomer = freshCustomerQuery.data ?? customer;

  const [endDialogOpen, setEndDialogOpen] = useState(false);

  // --- Recent-visit callback prompt ---
  // Same 30-day rule and banner as the Schedule step (Step 3); answering "Yes"
  // here carries the callback flag forward via the draft.
  const propertyEventsQuery = useEventBatchGetQuery({
    filters: {
      propertyId: property.id,
      isActive: 1,
      withoutLimit: true,
      orderBy: 'id',
      orderDir: 'DESC',
    },
    enabled: !!property.id,
  });
  const recentCompletedVisit = useRecentCompletedVisit(propertyEventsQuery.data?.results);

  // Dismissal is owned by JobCreationFlow so a "No" here persists into Step 3.
  const showCallbackPrompt =
    !!recentCompletedVisit && !callbackPromptDismissed && !isCallbackFlagged;

  const handleMarkAsCallback = () => {
    if (!recentCompletedVisit) return;
    // Flagging hides the prompt via isCallbackFlagged; don't hard-dismiss so it
    // re-appears if the callback flag is later removed.
    onMarkCallback?.(recentCompletedVisit.event.id);
  };

  const compact = useSchedulerStyle();

  // Warning only -- it does not gate the footer buttons or onContinue.
  const serviceAreaCheck = useOutsideServiceArea(property);

  // --- Render ---

  return (
    <div className={`flex flex-col ${compact ? 'gap-3' : 'gap-4'}`}>
      {/* Customer / Contract / Property card */}
      <div className="shrink-0">
        <ReviewCustomerCard
          customer={freshCustomer}
          property={property}
          contract={contract}
          contractStatus={contractStatus}
          isContractLoading={contractQuery.isLoading}
        />
      </div>

      {/* Outside service area (informational -- scheduling stays enabled) */}
      <OutsideServiceAreaBanner {...serviceAreaCheck} />

      {/* Notifications */}
      <div className="shrink-0">
        <ReviewNotifications
          customer={freshCustomer}
          property={property}
          onNotificationSaved={onNotificationSaved}
        />
      </div>

      {/* Internal Note */}
      <div className="shrink-0">
        <ReviewInternalNote customer={freshCustomer} />
      </div>

      {/* Recent-visit callback prompt (scheduler only) */}
      {compact && showCallbackPrompt && recentCompletedVisit && (
        <div className="shrink-0">
          <CallbackPrompt
            label={recentCompletedVisit.label}
            onYes={handleMarkAsCallback}
            onNo={() => onDismissCallbackPrompt?.()}
          />
        </div>
      )}

      {/* Service History / Proposals / Invoices */}
      <div>
        <ReviewServiceHistory propertyId={property.id} customer={freshCustomer} />
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 -mx-6 flex shrink-0 items-center justify-between border-t border-gray-200 bg-white px-6 pb-6 pt-4">
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setEndDialogOpen(true)}
            className={`border-primary bg-primary hover:border-primary hover:bg-primary/90 text-white hover:text-white ${compact ? 'font-semibold' : ''}`}
          >
            End Without Scheduling
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Continue as:</span>
          {(!allowedJobType || allowedJobType === 'service') && (
            <Button
              type="button"
              variant="outline"
              onClick={() => onContinue('service')}
              className="gap-1.5"
            >
              Service Job
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
          {(!allowedJobType || allowedJobType === 'project') && (
            <Button
              type="button"
              variant="outline"
              onClick={() => onContinue('project')}
              className="gap-1.5"
            >
              Project
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      <EndWithoutSchedulingDialog
        open={endDialogOpen}
        onOpenChange={setEndDialogOpen}
        customer={freshCustomer}
        onEnded={onEndWithoutScheduling ?? onBack}
      />
    </div>
  );
};
